import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Lock, Zap } from "lucide-react";
import { CHECKOUT_URL, HERO_IMG, HERO_FLOATING_IMG } from "../../data/academyData";
import { CountdownTimer } from "./CountdownTimer";
import { SpotsRemaining } from "./SpotsRemaining";

const headlineLines = ["Everything You Were", "Never Taught About", "Sex, Intimacy & Love."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
  <section id="top" ref={sectionRef} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20">
    <div className="absolute inset-0 -z-10">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <motion.img
          src={HERO_IMG}
          alt="Silhouette of a couple sharing an intimate moment at sunset"
          className="w-full h-full object-cover opacity-35"
          animate={{ scale: [1.04, 1.14, 1.04], x: [0, -14, 0], y: [0, 8, 0] }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
          data-testid="hero-ambient-bg"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      <motion.div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-rose-600/20 blur-[120px]"
        animate={{ opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="font-mono text-xs uppercase tracking-[0.3em] text-rose-400 font-semibold mb-6 flex items-center gap-2"
        data-testid="hero-urgency-badge"
      >
        <Zap size={14} /> First 100 Founding Members Only
      </motion.p>

      <h1 className="font-heading font-bold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.06] max-w-4xl">
        {headlineLines.map((line, i) => (
          <span key={line} className="block overflow-hidden">
            <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
              {i === 2 ? <span className="text-gradient-crimson">{line}</span> : line}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.85, duration: 0.8 }}
        className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed"
      >
        It's all here. And it changes everything. Whether you're single, dating, or deeply committed — this is the most honest, complete intimacy education you'll ever invest in.
      </motion.p>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }} className="mt-10 flex flex-wrap items-end gap-6">
        <div>
          <span className="text-zinc-500 line-through text-xl mr-3">₹9,999</span>
          <span className="font-heading text-5xl font-bold text-white">₹999</span>
        </div>
        <CountdownTimer />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.7 }} className="mt-8 flex flex-col sm:flex-row gap-4">
        <a
          href={CHECKOUT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="claim-founding-offer-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-rose-600 hover:bg-rose-500 text-white font-semibold text-base px-8 py-4 transition-all hover:scale-[1.03] shadow-[0_0_30px_rgba(229,9,20,0.45)]"
        >
          Claim Membership for ₹999 <ArrowRight size={18} />
        </a>
        <a
          href="#resources"
          data-testid="explore-guides-button"
          className="inline-flex items-center justify-center rounded-full border border-white/20 text-white font-medium text-base px-8 py-4 hover:bg-white/5 transition-colors"
        >
          Explore the 21 Secrets
        </a>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="mt-10">
        <SpotsRemaining />
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="mt-8 flex flex-wrap gap-6 text-zinc-400 text-sm">
        <span className="flex items-center gap-2"><Lock size={14} /> 100% Private & Confidential</span>
        <span className="flex items-center gap-2"><ShieldCheck size={14} /> 7-Day Guarantee</span>
        <span>🔞 For Adults 18+ Only</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.92, rotate: 5 }}
        animate={{ opacity: 1, scale: 1, rotate: 3 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="hidden 2xl:block absolute right-0 top-[4%] w-[25rem] rounded-2xl overflow-hidden border border-rose-500/30 shadow-[0_20px_70px_rgba(229,9,20,0.4)]"
      >
        <motion.img
          src={HERO_FLOATING_IMG}
          alt="A couple sharing a tender, intimate moment together"
          className="w-full h-[31rem] object-cover"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      </motion.div>
    </div>
  </section>
  );
};
